import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);

  // On mount: check sessionStorage first, then localStorage (for multi-tab support)
  useEffect(() => {
    const savedToken = sessionStorage.getItem('ems_token') || localStorage.getItem('ems_token');
    const savedUser = sessionStorage.getItem('ems_user') || localStorage.getItem('ems_user');
    if (savedToken && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(parsed);
        sessionStorage.setItem('ems_token', savedToken);
        sessionStorage.setItem('ems_user', savedUser);
        localStorage.setItem('ems_token', savedToken);
        localStorage.setItem('ems_user', savedUser);
      } catch {
        sessionStorage.clear();
        localStorage.removeItem('ems_token');
        localStorage.removeItem('ems_user');
      }
    }
    setLoading(false);
  }, []);

  // Sync logout across tabs via localStorage event
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'ems_token' && !e.newValue && token) {
        setToken(null);
        setUser(null);
        sessionStorage.removeItem('ems_token');
        sessionStorage.removeItem('ems_user');
        window.location.href = '/login';
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [token]);

  // Logout — defined before resetInactivityTimer since it depends on this
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    sessionStorage.removeItem('ems_token');
    sessionStorage.removeItem('ems_user');
    localStorage.removeItem('ems_token');
    localStorage.removeItem('ems_user');
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  }, []);

  // Inactivity timeout (8 hours)
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (token) {
      inactivityTimer.current = setTimeout(() => {
        logout();
        window.location.href = '/login?reason=timeout';
      }, INACTIVITY_TIMEOUT);
    }
  }, [token, logout]);

  useEffect(() => {
    if (!token) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [token, resetInactivityTimer]);

  const login = async (email, password, turnstileToken) => {
    const body = { email, password, turnstile_token: turnstileToken || null };
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    const t = data.access_token;
    const u = data.user;
    setToken(t);
    setUser(u);
    sessionStorage.setItem('ems_token', t);
    sessionStorage.setItem('ems_user', JSON.stringify(u));
    localStorage.setItem('ems_token', t);
    localStorage.setItem('ems_user', JSON.stringify(u));
    return data;
  };

  const hasRole = (role) => {
    if (!user?.roles) return false;
    if (Array.isArray(role)) return role.some(r => user.roles.includes(r));
    return user.roles.includes(role);
  };
  const hasMpRole = (role) => {
    if (!user?.mp_roles) return false;
    if (Array.isArray(role)) return role.some(r => user.mp_roles.includes(r));
    return user.mp_roles.includes(role);
  };
  const isParticipant = () => {
    const roles = user?.roles || [];
    return roles.includes('participant') || roles.includes('observer');
  };
  const isAdmin = () => {
    const roles = user?.roles || [];
    return roles.includes('super_admin') || roles.includes('hub_admin');
  };
  const isController = () => {
    const roles = user?.roles || [];
    return roles.includes('super_admin') || roles.includes('hub_admin') || roles.includes('simcel') || roles.includes('controller');
  };
  const isObserver = () => {
    const roles = user?.roles || [];
    return roles.includes('observer');
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, hasRole, hasMpRole, isParticipant, isAdmin, isController, isObserver }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
